'use memo'

import { useBalanceRosters } from '@sportninja/common/features/team-balance/hooks/useBalanceRosters'
import { useTeamPlayerFinderSettings } from '@sportninja/common/features/team/playerFinder/hooks/useTeamPlayerFinderSettings'
import {
  buildRegistrationSeasons,
  finderSeasonIds,
} from '@sportninja/common/features/team-balance/utils/registrationSeasons'
import type { RegistrationSeason } from '@sportninja/common/features/team-balance/utils/registrationSeasons'
import useTeamRosters from '@sportninja/common/hooks/useTeamRosters'
import type { RegistrationSubTab } from '../utils/registrationSubTabs'

interface UseRegistrationSeasonsOptions {
  teamId: string
  authUserId?: string | number | null
  /** The visible sub-tabs — only their sources are fetched and offered. */
  subTabs: readonly RegistrationSubTab[]
}

interface UseRegistrationSeasonsResult {
  /** Every season any visible sub-tab has, newest first. */
  seasons: RegistrationSeason[]
  /** Each sub-tab's own seasons, in its source's order (its old default first). */
  seasonIdsBySubTab: Record<RegistrationSubTab, string[]>
  /** Whether a sub-tab's source is still on its first load. */
  isLoadingBySubTab: Record<RegistrationSubTab, boolean>
}

/**
 * The Registration tab's Season picker data: the union of the three sub-tabs'
 * roster-keyed sources.
 *
 * Every source shares its react-query key with the sub-tab that reads it (and,
 * for Team Budget and Player Finder, with the Team page's tab probes), so the
 * shared picker costs no extra fetch. A source whose sub-tab is hidden is never
 * fetched — its seasons are not the caller's to pick.
 */
export const useRegistrationSeasons = ({
  teamId,
  authUserId = null,
  subTabs,
}: UseRegistrationSeasonsOptions): UseRegistrationSeasonsResult => {
  const budget = useBalanceRosters({
    teamId,
    authUserId,
    enabled: subTabs.includes('command-center'),
  })
  const billing = useTeamRosters({
    teamId,
    enabled: subTabs.includes('billing'),
  })
  const finder = useTeamPlayerFinderSettings({
    teamId,
    authUserId,
    enabled: subTabs.includes('player-finder'),
  })

  const seasons = buildRegistrationSeasons({
    teamRosters: billing.rosters,
    balanceRosters: budget.rosters,
    finderRegistrations: finder.registrations,
  })

  return {
    seasons,
    seasonIdsBySubTab: {
      'command-center': budget.rosters.map((roster) => roster.id),
      billing: billing.rosters.map((roster) => roster.id),
      'player-finder': finderSeasonIds(finder.registrations),
    },
    isLoadingBySubTab: {
      'command-center': budget.isLoading,
      billing: billing.isLoading,
      'player-finder': finder.isLoading,
    },
  }
}

export default useRegistrationSeasons
